/**
 * extrasFlag — the ONE cross-app read/write path for `lwa_extras_v1`, the
 * parent-enabled "Practice Playground" visibility flag (Reading / Writing /
 * Early Math extra practice behind a single button on the Foundations islands
 * page).
 *
 * WHY here in ui-shared: the parent portal (web-app) toggles this flag but
 * CANNOT import web-child (`@child`) at runtime, where the signed-envelope
 * writer (`flagGuard.setRigorFlag`) lives. So the reader AND a parent-gated
 * writer live here, over the shared `createSignedFlag` primitive (signedFlag.ts)
 * that replicates flagGuard.ts's scheme byte-for-byte — so an envelope written
 * here is accepted by flagGuard's `readGuardedFlag` (and vice-versa), and the
 * ops hook `window.__lwaRigorFlags.set('lwa_extras_v1', …)` stays interoperable.
 * `lwa_extras_v1` is registered in web-child/flagGuard.ts GUARDED_FLAGS +
 * DEFAULT_OFF_FLAGS (default OFF) too.
 *
 * DEFAULT OFF: an absent key reads false — shipping this changes nothing until a
 * parent signs it ON. A bare / legacy / foreign localStorage write ('on', '1',
 * 'true', …) is IGNORED; only a correctly PARENT-signed `{ v:'on' }` envelope
 * flips it on, so a child (or a stray script) can never enable it. Never throws.
 */

import { createSignedFlag } from './signedFlag';

export const EXTRAS_FLAG = 'lwa_extras_v1';

const flag = createSignedFlag(EXTRAS_FLAG); // DEFAULT OFF

/**
 * True when a PARENT is signed in on this device: a structurally valid,
 * unexpired `auth_token` JWT (the parent-portal session — the same identity
 * flagGuard.hasParentGate rides on). The child token (`child_auth_token`) does
 * NOT qualify.
 */
export function hasExtrasParentGate(): boolean {
  return flag.hasParentGate();
}

/**
 * Read the Practice Playground flag. Default OFF. Returns true ONLY for a
 * correctly parent-signed `{ v:'on', sig }` envelope; every other shape (absent,
 * bare 'on'/'1'/'true', malformed JSON, wrong/missing signature) reads false.
 * Never throws.
 */
export function isExtrasEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write. Turning it OFF (back to the default) clears the key;
 * turning it ON stores a signed `{ v:'on', sig }` envelope. Refuses (returns
 * false, writes nothing) without a live parent session — so a child-side or
 * stray write can never enable the feature.
 */
export function setExtrasEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check, so unit tests
 *  can exercise the enabled paths (mirrors flagGuard's __setGuardedFlagOnForTests). */
export function __setExtrasOnForTests(): void {
  flag.__setOnForTests();
}
